import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import axios from "axios";
import { useEffect, useState } from "react";
import { hostname, parseError } from "../../utils";
import {
  SERVICE_PACKAGE_TYPE_LABELS,
  SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION,
  SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY,
} from "../../constants";

export interface ServicePackage {
  id: string;
  name: string;
  description?: string;
  shortDescription?: string;
  packageType: string;
  price: number;
  currency: string;
  isActive: boolean;
  isFeatured: boolean;
  iconName?: string;
  featuresIncluded?: string[];
  billingType?: string;
  billingInterval?: string;
}

interface ServicePackagesStepProps {
  getToken: () => Promise<string>;
  selectedPackages: string[];
  onPackagesChange: (packageIds: string[]) => void;
  onContinue?: () => void;
}

const ServicePackagesStep: React.FC<ServicePackagesStepProps> = ({
  getToken,
  selectedPackages,
  onPackagesChange,
  onContinue,
}) => {
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [oneTimeTotal, setOneTimeTotal] = useState(0);
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const [annualTotal, setAnnualTotal] = useState(0);

  // Load available service packages
  useEffect(() => {
    const loadPackages = async () => {
      try {
        setPackagesLoading(true);
        setError(null);

        const response = await axios.get(`${hostname}/service-packages`, {
          params: { domain: window.location.hostname },
        });

        if (response.data.success) {
          setPackages(response.data.packages || []);
        } else {
          setError("Failed to load service packages");
        }
      } catch (err) {
        console.error("Error loading service packages:", err);
        setError(parseError(err));
      } finally {
        setPackagesLoading(false);
      }
    };

    loadPackages();
  }, []);

  // Calculate totals when selection changes
  useEffect(() => {
    const selectedPkgs = packages.filter((pkg) => selectedPackages.includes(pkg.id));

    const oneTime = selectedPkgs
      .filter((pkg) => pkg.billingType !== SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION)
      .reduce((sum, pkg) => sum + pkg.price, 0);

    const monthly = selectedPkgs
      .filter((pkg) => pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION &&
                       pkg.billingInterval === SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY)
      .reduce((sum, pkg) => sum + pkg.price, 0);

    const annual = selectedPkgs
      .filter((pkg) => pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION &&
                       pkg.billingInterval !== SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY)
      .reduce((sum, pkg) => sum + pkg.price, 0);

    setOneTimeTotal(oneTime);
    setMonthlyTotal(monthly);
    setAnnualTotal(annual);
  }, [selectedPackages, packages]);

  // If no packages available, auto-continue to next step
  useEffect(() => {
    if (packages.length === 0 && !packagesLoading && !error && onContinue) {
      onContinue();
    }
  }, [packages, packagesLoading, error, onContinue]);

  const handlePackageToggle = (packageId: string) => {
    if (selectedPackages.includes(packageId)) {
      onPackagesChange(selectedPackages.filter((id) => id !== packageId));
    } else {
      onPackagesChange([...selectedPackages, packageId]);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (packagesLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Optional Add-Ons
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="40%" height={40} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Use the buttons below to continue.
        </Typography>
      </Box>
    );
  }

  if (packages.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Optional Add-Ons
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enhance your experience with our professional services. These are
        one-time fees charged separately from your subscription.
      </Typography>

      <Grid container spacing={2}>
        {packages.map((pkg) => {
          const isSelected = selectedPackages.includes(pkg.id);

          return (
            <Grid item xs={12} md={6} lg={4} key={pkg.id}>
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  border: isSelected ? 2 : 1,
                  borderColor: isSelected ? "primary.main" : "divider",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                  },
                }}
                onClick={() => handlePackageToggle(pkg.id)}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isSelected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handlePackageToggle(pkg.id)}
                            color="primary"
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                      <Typography variant="h6" component="div">
                        {pkg.name}
                      </Typography>
                    </Box>
                    {pkg.isFeatured && (
                      <Chip
                        label="Popular"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Chip
                    label={
                      SERVICE_PACKAGE_TYPE_LABELS[pkg.packageType] ||
                      pkg.packageType
                    }
                    size="small"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, minHeight: 40 }}
                  >
                    {pkg.shortDescription || pkg.description}
                  </Typography>

                  <Typography
                    variant="h5"
                    color="primary"
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    {formatPrice(pkg.price)}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      {" "}
                      {pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION
                        ? pkg.billingInterval === SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY
                          ? "/month"
                          : "/year"
                        : "one-time"}
                    </Typography>
                  </Typography>

                  {pkg.featuresIncluded && pkg.featuresIncluded.length > 0 && (
                    <Box>
                      <Divider sx={{ my: 1 }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Includes:
                      </Typography>
                      {pkg.featuresIncluded.map((feature, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <CheckCircleOutlineIcon
                            sx={{ fontSize: 16, color: "primary.main" }}
                          />
                          <Typography variant="body2">{feature}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Summary */}
      {selectedPackages.length > 0 && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography variant="body1">
            {selectedPackages.length} package
            {selectedPackages.length > 1 ? "s" : ""} selected
          </Typography>
          {oneTimeTotal > 0 && (
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {formatPrice(oneTimeTotal)} one-time
            </Typography>
          )}
          {monthlyTotal > 0 && (
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {formatPrice(monthlyTotal)}/month
            </Typography>
          )}
          {annualTotal > 0 && (
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {formatPrice(annualTotal)}/year
            </Typography>
          )}
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {oneTimeTotal > 0 && (monthlyTotal > 0 || annualTotal > 0)
              ? "One-time fees charged now, recurring fees start with your subscription"
              : oneTimeTotal > 0
                ? "This will be charged after your subscription payment"
                : "Recurring fees start with your subscription"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ServicePackagesStep;
